import type { inferRouterOutputs } from '@trpc/server'
import {
  type KeyboardEvent,
  memo,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { AppRouter } from '@/api/_trpc/router'
import type { ContainerRef } from '@/lib/coaching/curriculum-query'
import {
  buildContainerTree,
  buildSourceRollups,
  mergeChildren,
  type SourceRollup,
} from '@/lib/coaching/source-tree'
import { trpc } from '@/lib/trpc'
import { useCoachingStore } from '../../store'
import type { CascadeDestination } from '../_lib/auto-place'
import {
  evalFilters,
  hasActiveFilterRules,
  type LibraryItem,
} from '../_lib/filter-engine'
import { pruneForest, sortSiblings } from '../_lib/source-tree-filter'
import {
  advanceTypeahead,
  expandableIdsOf,
  flattenVisibleRows,
  type TreeNavNode,
  type TypeaheadState,
  treeKeyCommand,
  type VisibleTreeRow,
} from '../_lib/source-tree-nav'
import { AssignmentActions } from './assignment-actions'
import {
  ContainerRowIcon,
  LeafRowIcon,
  SourceHeadingIcon,
} from './source-tree-icons'

type SourceLibrary =
  inferRouterOutputs<AppRouter>['admin']['coachingV2']['curriculum']['sourceLibrary']
type Source = SourceLibrary['sources'][number]
type Container = SourceLibrary['contents'][number]
type Membership = SourceLibrary['items'][number]

// Stable identity so the fallback does not remount consumers each render.
const EMPTY_ASSIGNED: ReadonlySet<string> = new Set()
const EMPTY_REVEAL: ReadonlySet<string> = new Set()

// Roving-focus ring for treeitem rows: the row div itself is the focusable
// element, so the ring must be declared here, not on an inner button.
const ROW_FOCUS_CLASS =
  'outline-none focus-visible:ring-1 focus-visible:ring-(--cd-text-muted)'

/**
 * One node's worth of domain data, carried on the shared forest (LG-827).
 * The LibraryItem projection is precomputed here so the filter, the sort,
 * and the row all read the same object — and so the memoized row only
 * re-renders when its node actually changes.
 */
type RowPayload =
  | { kind: 'source'; source: Source; item: LibraryItem }
  | {
      kind: 'container'
      container: Container
      rollup: SourceRollup
      item: LibraryItem
    }
  | { kind: 'item'; membership: Membership; item: LibraryItem }

const itemOf = (payload: RowPayload): LibraryItem => payload.item

/** "3h 20m" / "45m"; "+" marks a total with unknown contributors. */
function formatEstimate(minutes: number, partial: boolean): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const base =
    hours === 0 ? `${rest}m` : rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
  return partial ? `${base}+` : base
}

function containerMeta(container: Container, rollup: SourceRollup): string {
  const items = `${rollup.itemCount} item${rollup.itemCount === 1 ? '' : 's'}`
  // An authored estimate on the container is authoritative for itself; only
  // fall back to the computed subtotal, and only call it partial when the
  // fallback is the thing being shown.
  const minutes = container.estimatedMinutes ?? rollup.estimatedMinutes
  const partial =
    container.estimatedMinutes === null && rollup.hasUnknownEstimate
  if (minutes === 0 && partial) return `${items} · ~?`
  if (minutes === 0) return items
  return `${items} · ~${formatEstimate(minutes, partial)}`
}

// The projections carry `source` so a Browse "source is …" rule matches a
// source's whole subtree, not just its heading row — every rule in a
// conjunction is evaluated per node.
function sourceItem(source: Source): LibraryItem {
  return {
    id: `source-${source.id}`,
    catalogId: source.id,
    title: source.displayName,
    type: 'other',
    topic: '—',
    subtopic: 'Source',
    topicPairs: [],
    stage: 'Source',
    est: 0,
    estimatedMinutes: null,
    difficulty: '—',
    elo: null,
    extId: null,
    source: source.displayName,
    plans: [],
    companies: [],
  }
}

function membershipItem(
  membership: Membership,
  sourceName: string,
  assigned: ReadonlySet<string>,
): LibraryItem {
  return {
    id: membership.learningItemId,
    learningItemId: membership.learningItemId,
    catalogKind: membership.catalogKind,
    catalogId: membership.catalogId,
    title: membership.title,
    type: 'other',
    // A source membership has no problem taxonomy behind it — an explicit
    // empty stack, not an absent one, so `topicPairsOf` never fabricates a
    // pair out of these two display strings.
    topic: '—',
    subtopic: 'Curriculum source',
    topicPairs: [],
    stage: 'Source',
    est:
      membership.estimatedMinutes === null
        ? 0
        : membership.estimatedMinutes / 60,
    estimatedMinutes: membership.estimatedMinutes,
    difficulty: '—',
    elo: null,
    extId: membership.catalogId,
    source: sourceName,
    alreadyAssigned: assigned.has(membership.learningItemId),
    plans: [],
    companies: [],
  }
}

function containerItem(
  container: Container,
  rollup: SourceRollup,
  sourceName: string,
  assigned: ReadonlySet<string>,
): LibraryItem {
  const minutes = container.estimatedMinutes ?? rollup.estimatedMinutes
  return {
    id: `container-${container.id}`,
    // Null until the container has been assigned at least once — an
    // unmaterialized LearningItem simply cannot be already-assigned.
    learningItemId: container.learningItemId ?? undefined,
    catalogKind: 'source_content',
    catalogId: container.id,
    title: container.title,
    type: 'other',
    // No problem taxonomy behind a container either — see membershipItem.
    topic: '—',
    subtopic: container.kind,
    topicPairs: [],
    stage: 'Source',
    est: minutes / 60,
    estimatedMinutes: minutes === 0 ? null : minutes,
    difficulty: '—',
    elo: null,
    extId: container.id,
    source: sourceName,
    alreadyAssigned:
      container.learningItemId !== null &&
      assigned.has(container.learningItemId),
    plans: [],
    companies: [],
  }
}

const NO_CONTAINER_ASSIGN = (): void => {}

const EMPTY_ROLLUP: SourceRollup = {
  itemCount: 0,
  estimatedMinutes: 0,
  hasUnknownEstimate: false,
}

interface RowProps {
  row: VisibleTreeRow<RowPayload>
  active: boolean
  disabled: boolean
  registerRow: (element: HTMLDivElement | null) => (() => void) | undefined
  onFocusRow: (id: string) => void
  onToggleRow: (id: string) => void
  onAdd: (item: LibraryItem) => void
  onTray: (item: LibraryItem) => void
  /**
   * Assigning a container is a different command from adding an item: it fans
   * the container out into a card per member (LG-907), so it takes the
   * container rather than the row's `LibraryItem` stand-in.
   */
  onAssignContainer: (
    container: ContainerRef,
    destination: CascadeDestination,
  ) => void
}

/**
 * Depth guides: one vertical rule per ancestor level, so a deep row reads as
 * "inside" its chain rather than merely shifted right. Level 1 (sources) and
 * level 2 (source roots) draw none.
 */
function DepthGuides({ level }: { level: number }): ReactNode {
  if (level <= 2) return null
  return (
    <span aria-hidden="true" className="flex shrink-0 self-stretch">
      {Array.from({ length: level - 2 }, (_, index) => (
        <span
          // Guides are pure positional decoration; nothing reorders them.
          // biome-ignore lint/suspicious/noArrayIndexKey: static positional spans
          key={index}
          className="w-4 shrink-0 border-l border-(--cd-border-2)"
        />
      ))}
    </span>
  )
}

/**
 * One visible row of the tree. Memoized: arrow-key focus moves change only
 * the two affected rows' `active` prop, so key-repeat over a fully expanded
 * tree re-renders two rows, not every one (LG-827; flagged in PR 1's review).
 */
const TreeRow = memo(function TreeRow(props: RowProps): ReactNode {
  const {
    row,
    active,
    disabled,
    registerRow,
    onFocusRow,
    onToggleRow,
    onAdd,
    onTray,
    onAssignContainer,
  } = props
  const payload = row.payload

  if (payload.kind === 'source') {
    // The whole heading row is the disclosure control; unlike container rows
    // it hosts no action buttons, so a row-level click cannot swallow
    // anything else. Sticky: the heading holds the top of the scroll pane
    // while its subtree scrolls under it.
    return (
      <div
        role="treeitem"
        aria-label={payload.source.displayName}
        aria-level={row.level}
        aria-expanded={row.hasChildren ? row.expanded : undefined}
        tabIndex={active ? 0 : -1}
        data-row-id={row.id}
        ref={registerRow}
        onFocus={() => onFocusRow(row.id)}
        onClick={() => onToggleRow(row.id)}
        className={`sticky top-0 z-10 flex cursor-pointer select-none items-center gap-1.5 rounded bg-(--cd-shell) px-1 pt-1.5 pb-1 text-[11px] font-bold tracking-[0.05em] text-(--cd-text-muted) ${ROW_FOCUS_CLASS}`}
      >
        <span aria-hidden="true" className="w-3 shrink-0 text-[11px]">
          {row.expanded ? '▾' : '▸'}
        </span>
        <SourceHeadingIcon icon={payload.source.icon} />
        {payload.source.displayName}
      </div>
    )
  }

  if (payload.kind === 'item') {
    const item = payload.item
    return (
      <div
        role="treeitem"
        aria-label={payload.membership.title}
        aria-level={row.level}
        tabIndex={active ? 0 : -1}
        data-row-id={row.id}
        ref={registerRow}
        onFocus={() => onFocusRow(row.id)}
        className={`flex scroll-mt-8 items-center gap-2 rounded-lg border border-transparent px-2.5 py-1 ${ROW_FOCUS_CLASS}`}
      >
        <DepthGuides level={row.level} />
        <span aria-hidden="true" className="w-4 shrink-0" />
        <LeafRowIcon kind={payload.membership.catalogKind} />
        <span className="min-w-0 flex-1 text-xs">
          <span className="text-(--cd-text)">{payload.membership.title}</span>{' '}
          <span className="text-[10.5px] text-(--cd-text-faint)">
            {payload.membership.catalogKind}
          </span>
        </span>
        <AssignmentActions
          variant="standard"
          disabled={disabled || item.alreadyAssigned === true}
          disabledHelp={
            item.alreadyAssigned
              ? 'Already in this plan — complete or cancel it to add again'
              : undefined
          }
          addLabel={`Add ${payload.membership.title}`}
          trayLabel={`Tray ${payload.membership.title}`}
          tabbable={active}
          onAdd={() => onAdd(item)}
          onTray={() => onTray(item)}
        />
      </div>
    )
  }

  // `payload.item` is the container's `LibraryItem` stand-in, which exists for
  // the filter evaluator; the row itself assigns the container, not an item.
  const { container, rollup } = payload
  return (
    <div
      role="treeitem"
      aria-label={`${container.title} · ${containerMeta(container, rollup)}`}
      aria-level={row.level}
      aria-expanded={row.hasChildren ? row.expanded : undefined}
      tabIndex={active ? 0 : -1}
      data-row-id={row.id}
      ref={registerRow}
      onFocus={() => onFocusRow(row.id)}
      className={`flex scroll-mt-8 items-center gap-2 rounded-lg border border-(--cd-border) bg-(--cd-inset) px-2.5 py-1.5 ${ROW_FOCUS_CLASS}`}
    >
      <DepthGuides level={row.level} />
      {row.hasChildren ? (
        <button
          type="button"
          aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${container.title}`}
          aria-expanded={row.expanded}
          // Out of the tab order: the roving treeitem already expands and
          // collapses from the keyboard, so a tabbable caret would bring
          // back one extra stop per row.
          tabIndex={-1}
          onClick={() => onToggleRow(row.id)}
          className="w-4 shrink-0 text-[11px] text-(--cd-text-muted)"
        >
          {row.expanded ? '▾' : '▸'}
        </button>
      ) : (
        <span aria-hidden="true" className="w-4 shrink-0" />
      )}
      <ContainerRowIcon icon={container.icon} kind={container.kind} />
      <span className="min-w-0 flex-1 text-xs">
        <strong className="font-semibold text-(--cd-text)">
          {container.title}
        </strong>{' '}
        <span className="text-[10.5px] text-(--cd-text-faint)">
          {containerMeta(container, rollup)}
        </span>
      </span>
      <span className="rounded-md border border-(--cd-border) bg-(--cd-bg) px-1.5 py-0.5 text-[10px] font-bold tracking-[0.05em] text-(--cd-purple)">
        {container.kind}
      </span>
      {/*
        Not offered at all on a container that only groups (LG-907). A disabled
        pair would still be an affordance on every row of a whole book, and the
        answer to "why can't I assign this?" is visible one level down: its
        chapters can be.
      */}
      {container.isAssignableUnit ? (
        <AssignmentActions
          variant="standard"
          disabled={disabled}
          addLabel={`Add all of ${container.title}`}
          trayLabel={`Tray ${container.title}`}
          tabbable={active}
          onAdd={() =>
            onAssignContainer(
              { sourceContentId: container.id, title: container.title },
              'auto',
            )
          }
          onTray={() =>
            onAssignContainer(
              { sourceContentId: container.id, title: container.title },
              'tray',
            )
          }
        />
      ) : null}
    </div>
  )
})

function LoadingSkeleton(): ReactNode {
  // Shaped like the tree it replaces: a heading bar, then indented rows.
  const widths = ['w-32', 'w-3/5', 'w-2/5', 'w-1/2', 'w-28', 'w-3/5', 'w-2/5']
  return (
    <div role="status" className="animate-pulse space-y-1.5 px-1 py-2">
      <span className="sr-only">Loading source trees…</span>
      {widths.map((width, index) => (
        <div
          // Static placeholder list; nothing reorders it.
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
          key={index}
          aria-hidden="true"
          className={`h-6 rounded-lg bg-(--cd-inset) ${width} ${
            index % 4 === 0 ? '' : 'ml-6'
          }`}
        />
      ))}
    </div>
  )
}

export function SourcesTab(): ReactNode {
  const { curriculum } = useCoachingStore()

  if (curriculum.persisted !== true) {
    return (
      <p className="rounded-lg border border-dashed border-(--cd-border-2) px-3 py-4 text-xs text-(--cd-text-faint)">
        Source trees require the persisted curriculum.
      </p>
    )
  }

  return <PersistedSourcesTree />
}

// Split out from SourcesTab so the tRPC hook below is only ever mounted once
// the curriculum is persisted. Hooks cannot be called conditionally, and
// mock-mode tests render the dashboard with no tRPC context at all — calling
// this hook unconditionally in SourcesTab would crash them even though they
// never open this tab's data.
function PersistedSourcesTree(): ReactNode {
  const { curriculum } = useCoachingStore()
  const assigned = curriculum.assignedLearningItemIds ?? EMPTY_ASSIGNED
  // Persisted-only on the contract (like `sourceTreeOpen`), and this component
  // only ever renders behind the persisted check above — so the fallback is a
  // type obligation, not a state the tree can reach.
  const assignContainer = curriculum.assignContainer ?? NO_CONTAINER_ASSIGN
  const query = trpc.admin.coachingV2.curriculum.sourceLibrary.useQuery()

  // Expansion lives on the curriculum slice so it survives library-tab
  // switches (LG-312). The fields are optional persisted-only contract
  // members (like toggleWeekFold), and this component only mounts behind the
  // persisted gate, so the optional chain below is a type-level formality.
  const storedOpen = curriculum.sourceTreeOpen ?? null
  const setOpen = (next: ReadonlySet<string>): void =>
    curriculum.setSourceTreeOpen?.(next)

  const [focusedId, setFocusedId] = useState<string | null>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  const typeahead = useRef<TypeaheadState>({ buffer: '', at: 0 })

  // THE tree: one forest carries the domain payloads, and everything else —
  // filtering, sorting, the rendered rows, and the keyboard model — derives
  // from it, so no two traversals can drift apart (LG-827).
  const forest = useMemo<TreeNavNode<RowPayload>[]>(() => {
    const data = query.data
    if (!data) return []
    const itemsByContainer = new Map<string, Membership[]>()
    for (const membership of data.items) {
      const bucket = itemsByContainer.get(membership.sourceContentId)
      if (bucket) bucket.push(membership)
      else itemsByContainer.set(membership.sourceContentId, [membership])
    }
    return data.sources
      .map((source) => {
        const contents = data.contents.filter(
          (content) => content.sourceId === source.id,
        )
        const containersByParent = buildContainerTree(contents)
        const rollups = buildSourceRollups(containersByParent, itemsByContainer)
        const nodesUnder = (
          parentId: string | null,
        ): TreeNavNode<RowPayload>[] =>
          mergeChildren(
            containersByParent.get(parentId) ?? [],
            parentId === null ? [] : (itemsByContainer.get(parentId) ?? []),
          ).map((child) => {
            if (child.type === 'item') {
              const membership = child.value
              return {
                id: membership.id,
                title: membership.title,
                payload: {
                  kind: 'item',
                  membership,
                  item: membershipItem(
                    membership,
                    source.displayName,
                    assigned,
                  ),
                },
                children: [],
              }
            }
            const container = child.value
            const rollup = rollups.get(container.id) ?? EMPTY_ROLLUP
            return {
              id: container.id,
              title: container.title,
              payload: {
                kind: 'container',
                container,
                rollup,
                item: containerItem(
                  container,
                  rollup,
                  source.displayName,
                  assigned,
                ),
              },
              children: nodesUnder(container.id),
            }
          })
        return {
          source,
          node: {
            id: source.id,
            title: source.displayName,
            payload: {
              kind: 'source' as const,
              source,
              item: sourceItem(source),
            },
            children: nodesUnder(null),
          },
        }
      })
      .filter((entry) => entry.node.children.length > 0)
      .map((entry) => entry.node)
  }, [query.data, assigned])

  // Browse's filter rules, applied tree-aware: a match keeps its subtree and
  // its ancestor chain (pruneForest). Rules evaluate against the same
  // LibraryItem projections the flat tabs use. "Filtering" means at least
  // one rule is complete — a freshly added blank rule is inert (it neither
  // narrows the tree nor springs it open), mirroring the engine's
  // incomplete-rule fold.
  const filters = curriculum.filters
  const filtersActive = hasActiveFilterRules(filters)
  const pruned = useMemo(
    () =>
      filtersActive
        ? pruneForest(forest, (payload) => evalFilters(filters, payload.item))
        : { forest, revealIds: EMPTY_REVEAL },
    [forest, filters, filtersActive],
  )

  // Sibling-only sorting; no rules = authored order (see sortSiblings).
  const sorts = curriculum.sorts
  const viewForest = useMemo(
    () => sortSiblings(pruned.forest, sorts, itemOf),
    [pruned, sorts],
  )

  // Untouched state = the default view: every source open, containers closed.
  const open = useMemo<ReadonlySet<string>>(
    () => storedOpen ?? new Set(forest.map((node) => node.id)),
    [storedOpen, forest],
  )

  // While a filter is active, matches must be visible: the stored set is
  // joined by every ancestor of a match, and those revealed ancestors stay
  // pinned open (toggleRow below refuses to collapse them — the filter
  // needs them open to show the match). The union is derived only; the
  // stored set is never widened, so clearing the filter restores the
  // coach's own expansion state.
  const effectiveOpen = useMemo<ReadonlySet<string>>(() => {
    if (pruned.revealIds.size === 0) return open
    const joined = new Set(open)
    for (const id of pruned.revealIds) joined.add(id)
    return joined
  }, [open, pruned])

  const rows = useMemo(
    () => flattenVisibleRows(viewForest, effectiveOpen),
    [viewForest, effectiveOpen],
  )

  // Exactly one tree row carries tabIndex 0; a vanished focus falls back to
  // the first row rather than dropping the tree out of the tab order.
  const focusId =
    focusedId !== null && rows.some((row) => row.id === focusedId)
      ? focusedId
      : (rows[0]?.id ?? null)

  // One stable callback for every row (ids travel on data-row-id) so a render
  // re-attaches no refs; the returned cleanup keeps unmounted rows out of the
  // map so a stale entry can never receive focus.
  const registerRow = useCallback(
    (element: HTMLDivElement | null): (() => void) | undefined => {
      const id = element?.dataset.rowId
      if (element == null || id === undefined) return undefined
      rowRefs.current.set(id, element)
      return () => {
        rowRefs.current.delete(id)
      }
    },
    [],
  )

  const focusRow = useCallback((id: string): void => {
    setFocusedId(id)
    rowRefs.current.get(id)?.focus()
  }, [])

  // Pointer toggles move the roving focus onto their row first: browsers
  // disagree on whether clicking the caret button focuses it (Safari never
  // does), so focus is set explicitly instead of trusting the focus event.
  // Toggling is deliberately inseparable from that focus handoff — a bare
  // toggle wired to some future affordance would re-create the Safari bug.
  //
  // Direction is decided from effectiveOpen — the set the coach SEES — not
  // the stored set. A filter-revealed ancestor renders expanded while
  // absent from the stored set; reading the stored set there would ADD the
  // id, silently persisting the reveal. Collapsing such a row is instead a
  // no-op: it stays pinned while the filter needs it open.
  const setSliceOpen = curriculum.setSourceTreeOpen
  const toggleRow = useCallback(
    (id: string): void => {
      focusRow(id)
      const next = new Set(open)
      if (effectiveOpen.has(id)) {
        // Refused even when the coach's own expansion also holds the row
        // open: deleting the stored id would look like a no-op now, then
        // collapse the row the moment the filter clears.
        if (pruned.revealIds.has(id)) return // pinned by a filter reveal
        next.delete(id)
      } else {
        next.add(id)
      }
      setSliceOpen?.(next)
    },
    [focusRow, open, effectiveOpen, pruned.revealIds, setSliceOpen],
  )

  const setNodeOpen = (id: string, value: boolean): void => {
    if (!value && pruned.revealIds.has(id)) return // pinned by a filter reveal
    const next = new Set(open)
    if (value) next.add(id)
    else next.delete(id)
    setOpen(next)
  }

  // Guarded while nothing is loaded: expand-all over an empty forest would
  // persist an EMPTY open-set to the slice, and the `storedOpen ?? default`
  // above would then render every later-loaded source collapsed all session.
  const expandAll = (): void => {
    if (forest.length > 0) setOpen(expandableIdsOf(forest))
  }
  const collapseAll = (): void => {
    if (forest.length > 0) setOpen(new Set())
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof HTMLElement)) return
    if (focusId === null) return
    // Keys land here from the row's action buttons too (events bubble). A
    // focused button keeps Enter and Space — its activation keys — but
    // arrows, Home/End, and type-ahead mean tree navigation wherever they
    // were pressed, and hand focus back to a row.
    const onTreeitem = event.target.getAttribute('role') === 'treeitem'
    if (!onTreeitem && (event.key === 'Enter' || event.key === ' ')) return
    // Modified keys are platform shortcuts (Alt+Left is Back, Cmd+arrows
    // scroll) — never tree navigation.
    if (event.ctrlKey || event.metaKey || event.altKey) return

    if (event.key.length === 1 && event.key !== ' ') {
      const advanced = advanceTypeahead(
        rows,
        focusId,
        typeahead.current,
        event.key,
        Date.now(),
      )
      typeahead.current = advanced.state
      if (advanced.targetId !== null) {
        event.preventDefault()
        focusRow(advanced.targetId)
      }
      return
    }

    const command = treeKeyCommand(rows, focusId, event.key)
    // Null = not the tree's key at all; anything else is consumed, even a
    // no-op (Space on a leaf, ArrowUp on the first row) — the default would
    // scroll the pane away from the focus.
    if (command === null) return
    event.preventDefault()
    if (command.kind === 'none') return
    if (command.kind === 'focus') focusRow(command.id)
    else setNodeOpen(command.id, command.kind === 'expand')
  }

  const noMatches =
    filtersActive && forest.length > 0 && viewForest.length === 0

  return (
    <div className="space-y-2 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <div className="flex items-center gap-2 xl:shrink-0">
        <p className="min-w-0 flex-1 text-[11px] text-(--cd-text-faint)">
          Books → chapters → sections; courses → modules → tasks. Assign
          anything from a whole chapter to one quiz.
        </p>
        <button
          type="button"
          onClick={expandAll}
          disabled={forest.length === 0}
          className="whitespace-nowrap text-[11px] font-semibold text-(--cd-text-muted) hover:text-(--cd-text) disabled:opacity-50 disabled:hover:text-(--cd-text-muted)"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          disabled={forest.length === 0}
          className="whitespace-nowrap text-[11px] font-semibold text-(--cd-text-muted) hover:text-(--cd-text) disabled:opacity-50 disabled:hover:text-(--cd-text-muted)"
        >
          Collapse all
        </button>
      </div>
      <div className="flex max-h-[340px] flex-col overflow-y-auto xl:max-h-none xl:min-h-0 xl:flex-1">
        {query.isLoading ? (
          <LoadingSkeleton />
        ) : query.error ? (
          <p className="px-3 py-4 text-xs text-(--cd-text-faint)">
            Source trees could not be loaded.
          </p>
        ) : forest.length === 0 ? (
          <p className="rounded-lg border border-dashed border-(--cd-border-2) px-3 py-4 text-xs text-(--cd-text-faint)">
            No source containers have been authored yet.
          </p>
        ) : noMatches ? (
          <p className="rounded-lg border border-dashed border-(--cd-border-2) px-3 py-4 text-xs text-(--cd-text-faint)">
            No sources match the current filters. Clear or loosen the filter
            rules to see the trees again.
          </p>
        ) : (
          <div
            role="tree"
            aria-label="Source library"
            onKeyDown={handleKeyDown}
            className="flex flex-col gap-0.5"
          >
            {rows.map((row) => (
              <TreeRow
                key={row.id}
                row={row}
                active={focusId === row.id}
                disabled={curriculum.stagePending === true}
                registerRow={registerRow}
                onFocusRow={setFocusedId}
                onToggleRow={toggleRow}
                onAdd={curriculum.addAuto}
                onTray={curriculum.stageToTray}
                onAssignContainer={assignContainer}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
